import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import PageSeo from "../../components/PageSeo";
import { useAppSubmitting } from "../../hooks/useAppSubmitting";
import { PartyAccessBadge, PartyCoinBadge, PartyTopBar } from "../../party/PartyChrome";
import { PartyMascot } from "../../party/PartyMascot";
import { PartyPlayFormatChips } from "../../party/PartyPlayFormatChips";
import { PartyTvCastGlyph } from "../../party/PartyTvCastGlyph";
import { PARTY_ONLINE_SHELL_IDS } from "../../party/modePartyCards";
import { usePartyShellGames } from "../../party/usePartyShellGames";
import type { PartyShellPalette } from "../../theme/theme";
import { MixpanelEvents, trackEvent } from "../../utils/mixpanel";
import { absoluteUrl } from "../../utils/siteOrigin";

const PageIntro = styled(Stack)(({ theme }) => {
  const p = (theme.palette as unknown as { party: PartyShellPalette }).party;
  return {
    padding: theme.spacing(0.5, 2.5, 2),
    "& .party-display": {
      fontSize: 36,
      fontWeight: 900,
      lineHeight: 0.95,
    },
    "& .subtitle": {
      color: p.inkDim,
      marginTop: theme.spacing(1),
      fontSize: 14,
    },
  };
});

const ModeGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.5, 2.5, 5),
}));

const ModeCard = styled(ButtonBase)(({ theme }) => ({
  appearance: "none",
  border: 0,
  color: "var(--ink)",
  fontFamily: "inherit",
  padding: theme.spacing(1.75),
  cursor: "pointer",
  textAlign: "start",
  minHeight: 210,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  position: "relative",
  width: "100%",
  borderRadius: "var(--r-card)",
  overflow: "hidden",
  isolation: "isolate",
}));

const TvChip = styled("span")({
  display: "inline-flex",
  padding: "2px 6px",
  lineHeight: 0,
});

/** Keeps header row width stable when the paid price chip is hidden during app submit. */
const PaidBadgePlaceholder = styled(Box)(({ theme }) => ({
  minWidth: 52,
  minHeight: 28,
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    minWidth: 48,
  },
}));

export default function PartyModesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const p = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const { games, credits, isLoading } = usePartyShellGames();
  const { isAppSubmitting } = useAppSubmitting();
  const [modesFilter, setModesFilter] = useState<"all" | "online">("all");

  const filteredGames = useMemo(() => {
    if (modesFilter === "online") return games.filter((g) => PARTY_ONLINE_SHELL_IDS.has(g.id));
    return games;
  }, [games, modesFilter]);

  const modesJsonLd = useMemo(() => {
    const canonical = absoluteUrl("/party/modes");
    if (!canonical.startsWith("http")) return undefined;
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: t("seo.partyModes.title"),
      description: t("seo.partyModes.description"),
      url: canonical,
      inLanguage: i18n.language,
      isPartOf: {
        "@type": "WebSite",
        name: "Kan",
        alternateName: ["كان"],
        url: absoluteUrl("/"),
      },
    };
  }, [t, i18n.language]);

  useEffect(() => {
    trackEvent(MixpanelEvents.PARTY_MODES_VIEWED, {
      game_count: games.length,
      filter: modesFilter,
    });
  }, [games.length, modesFilter]);

  return (
    <Box dir={i18n.language === "ar" ? "rtl" : "ltr"} lang={i18n.language || "ar"}>
      <PageSeo
        titleKey="seo.partyModes.title"
        descriptionKey="seo.partyModes.description"
        path="/party/modes"
        jsonLd={modesJsonLd}
      />
      <PartyTopBar
        title={t("party.modes.topBarTitle")}
        onBack={() => navigate("/")}
        right={<PartyCoinBadge amount={credits} />}
      />
      <PageIntro spacing={0}>
        <Typography component="div" className="party-display">
          {t("party.modes.headline")}
        </Typography>
        <Typography component="div" className="subtitle">
          {t("party.modes.subtitle")}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ px: 2.5, pt: 1.5, flexWrap: "wrap" }}>
          <button
            type="button"
            className="party-chip"
            onClick={() => setModesFilter("all")}
            style={{
              cursor: "pointer",
              fontWeight: 800,
              border: modesFilter === "all" ? "2px solid var(--hot)" : "1px solid var(--ink-faint)",
              opacity: modesFilter === "all" ? 1 : 0.85,
            }}
          >
            {t("party.modes.filterAll")}
          </button>
          <button
            type="button"
            className="party-chip"
            onClick={() => setModesFilter("online")}
            style={{
              cursor: "pointer",
              fontWeight: 800,
              border: modesFilter === "online" ? "2px solid var(--hot)" : "1px solid var(--ink-faint)",
              opacity: modesFilter === "online" ? 1 : 0.85,
            }}
          >
            {t("party.modes.filterOnline")}
          </button>
        </Stack>
        {modesFilter === "online" ? (
          <Typography sx={{ px: 2.5, pt: 1, fontSize: 13, color: p.inkDim, lineHeight: 1.45 }}>
            {t("party.modes.filterOnlineHint")}
          </Typography>
        ) : null}
      </PageIntro>

      {isLoading && games.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center", color: p.inkDim }}>…</Box>
      ) : filteredGames.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center", color: p.inkDim, px: 2.5 }}>{t("party.modes.filterOnlineHint")}</Box>
      ) : (
        <ModeGrid>
          {filteredGames.map((g, i) => (
            <ModeCard
              key={g.id}
              type="button"
              onClick={() => {
                trackEvent(MixpanelEvents.PARTY_GAME_CARD_CLICKED, {
                  shell_id: g.id,
                  access: g.access,
                  position: i,
                  consumes_credit: g.consumesCredit,
                });
                navigate(`/party/detail/${g.id}`);
              }}
              className="party-kan-card party-anim-card-deal"
              style={
                {
                  ["--card-bg" as string]: g.bg,
                  animationDelay: `${i * 40}ms`,
                } as React.CSSProperties
              }
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                  <Typography
                    component="span"
                    className="party-mono"
                    sx={{ fontSize: 10, color: p.inkDim, letterSpacing: "0.1em", flexShrink: 0 }}
                  >
                    #{String(i + 1).padStart(2, "0")}
                  </Typography>
                  <TvChip className="party-chip">
                    <PartyTvCastGlyph support={g.supportsTvCast} />
                  </TvChip>
                </Stack>
                {g.access === "paid" && isAppSubmitting && g.consumesCredit ? (
                  <PaidBadgePlaceholder aria-hidden />
                ) : (
                  <PartyAccessBadge access={g.access} price={g.price} />
                )}
              </Stack>

              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <PartyMascot shellId={g.id} size={92} />
              </Box>

              <Box>
                <Typography component="div" className="party-display" sx={{ fontSize: 26, lineHeight: 1, textAlign: "center" }}>
                  {g.nameAr}
                </Typography>
                <Typography sx={{ fontSize: 11, color: p.inkDim, mt: 0.5, textAlign: "center" }}>{g.tag}</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                  <Typography component="span" className="party-chip" sx={{ fontSize: 10, fontWeight: 700 }}>
                    {g.playersTag}
                  </Typography>
                  <PartyPlayFormatChips format={g.playFormat} />
                </Stack>
              </Box>
            </ModeCard>
          ))}
        </ModeGrid>
      )}
    </Box>
  );
}
